import React, { useState, ReactNode, useRef, FC, useMemo } from 'react';
import { extent, max } from 'd3-array';
import { scaleLinear, scaleThreshold } from 'd3-scale';

import ZoomPercentageBar from './zoom/ZoomPercentageBar';
import {
  TooltipFn,
  TooltipPoint,
  WorkloadConcentrationSeries,
  WorkloadConcentrationPoint,
} from '../../interfaces/Charts';
import { ChartTooltip } from './tooltip/ChartTooltip';
import {
  GraphLayoutGrid,
  GraphLayoutGraph,
  GraphLayoutBottomLabel,
  GraphLayoutLeftLabelText,
  GraphLayoutLeftLabel,
  GraphLayoutHeader,
  GraphLayoutHeaderText,
  GraphLayoutHeaderLegend,
} from './ChartPanel';
import { ChartPopover } from './tooltip/ChartPopover';

import { useTooltip, useResize } from './hooks';
import { DEFAULT_PADDING } from './helpers/config';
import { VerticalLine, HorizontalLine } from './shapes';
import { VerticalLeftValueAxis, HorizontalPercentageAxis } from './axis';
import styled from '@emotion/styled';

export interface WorkloadConcentrationChartProps {
  title: ReactNode;
  titleMessage?: ReactNode;
  series: WorkloadConcentrationSeries;
  thresholdValue: number;
  popoverText?: ReactNode;
  tooltipFn?: TooltipFn<WorkloadConcentrationPoint>;
  legend?: ReactNode;
  leftAxisLabel?: string;
  height?: number;
  width?: number;
  annotationText?: ReactNode;
}

const WorkloadLabel = styled.h4`
  color: #0076ce;
  text-align: center;
  width: 100%;
  font-size: 1.25rem;
  letter-spacing: 0.2px;
  font-weight: normal;
`;
const WorkloadBottomLabel = styled(WorkloadLabel)`
  margin-top: 0.125rem;
`;

const WorkloadCircle = styled.circle`
  opacity: 0.65;
  &:hover {
    opacity: 1;
  }
`;

const WorkloadAnnotationText = styled.div`
  grid-column: 5;
  -ms-grid-column: 5;
  grid-row: 2;
  -ms-grid-row: 2;
  color: #566072;
  letter-spacing: 0.32px;
  font-size: 1rem;
`;

const TitleMessage = styled.p`
  margin: 0 0 0 0.7rem;
  color: #566072;
  font-weight: 400;
  font-size: 0.9rem;
`;

export const WorkloadConcentrationChart: FC<WorkloadConcentrationChartProps> = props => {
  const graphContainer = useRef<HTMLDivElement>(null);
  const height = props.height ? props.height : 350;
  const width = useResize(graphContainer, props.width);

  const padding = DEFAULT_PADDING;

  const data = props.series.points.sort((a, b) => {
    return a.capacityInGb > b.capacityInGb ? -1 : a.capacityInGb < b.capacityInGb ? 1 : 0;
  });

  // sort data so circles appear in the correct order

  const [state, setState] = useState({
    zoomedScale: scaleLinear()
      .domain([0, 100])
      .range([0, width]),
  });

  // Calculate maxIops to data-drive the yScale.
  // Rounds to nearest 10, to prevent clipping ticks.
  const maxIops = useMemo(() => {
    const upperBounds = max(data, d => d.iopsPercentage);
    return upperBounds ? Math.ceil(upperBounds / 10) * 10 : 100;
  }, [data]);

  const yScale = scaleLinear()
    .domain([0, maxIops])
    .range([height - padding.top, padding.bottom]);

  const colorScale = scaleThreshold<number, string>()
    .domain([0, 2, 10])
    .range(['#AAAAAA', '#009E3B', '#F2AF00', '#E22102']);

  const radiusExtent = extent(data, d => d.capacityInGb) as [number, number];
  const radiusScale = scaleLinear()
    .domain(radiusExtent)
    .range([10, 45]);

  const onZoom = (x1: number, x2: number) => {
    if (x1 >= 0 && x2 > 0 && width > 0) {
      const zoomScale = scaleLinear()
        .domain([x1, x2])
        .range([0, width]);

      setState({ zoomedScale: zoomScale });
    } else {
      const extent = [0, 100];
      const zoomScale = scaleLinear()
        .domain([...extent])
        .range([0, width]);
      setState({ zoomedScale: zoomScale });
    }
  };

  const { hideTooltip, showTooltip, tooltip } = useTooltip(props.tooltipFn);

  const plottedPoints: TooltipPoint<WorkloadConcentrationPoint>[] = data.map((d, i) => {
    return {
      x: state.zoomedScale(d.rollingCapacityPercentage),
      y: yScale(d.iopsPercentage),
      index: i,
      series: props.series,
    };
  });

  const showTooltipOnTopOfBubble = (p: TooltipPoint<WorkloadConcentrationPoint>) => {
    const pointatTopOfCircle: TooltipPoint<WorkloadConcentrationPoint> = {
      ...p,
      y: p.y - radiusScale(p.series.points[p.index].capacityInGb),
    };
    return showTooltip(pointatTopOfCircle);
  };

  const yTicks = yScale.ticks();

  return (
    <GraphLayoutGrid>
      <GraphLayoutGraph ref={graphContainer}>
        <svg
          height={height}
          width="100%"
          preserveAspectRatio="xMidYMax meet"
          viewBox={`0,0,${width},${height}`}
        >
          <defs>
            <clipPath id="chart-clip-path">
              <rect
                x="0"
                y={yScale(yTicks[yTicks.length - 1]) - 0.5}
                width={width}
                height={yScale(yTicks[0]) - yScale(yTicks[yTicks.length - 1]) + 0.5}
              />
            </clipPath>
          </defs>

          <g clipPath="url(#chart-clip-path)">
            {yScale.ticks().map((tick, i) => (
              <HorizontalLine key={i} color="#E4E4E4" y={yScale(tick)} width={width} />
            ))}
            {state.zoomedScale.ticks().map((tick, i) => (
              <VerticalLine key={i} color="#E4E4E4" x={state.zoomedScale(tick)} height={height} />
            ))}
            {plottedPoints.map((p, i) => {
              return (
                <WorkloadCircle
                  key={i}
                  cx={p.x}
                  cy={p.y}
                  onMouseEnter={() => showTooltipOnTopOfBubble(p)}
                  onMouseLeave={() => hideTooltip()}
                  r={radiusScale(p.series.points[p.index].capacityInGb)}
                  fill={colorScale(p.series.points[p.index].latency)}
                ></WorkloadCircle>
              );
            })}
            <VerticalLine
              color="#85be15"
              strokeWidth="2px"
              x={state.zoomedScale(props.thresholdValue)}
              height={height}
            />
          </g>
        </svg>
        <ChartTooltip {...tooltip} />
      </GraphLayoutGraph>
      {props.annotationText && (
        <WorkloadAnnotationText>{props.annotationText}</WorkloadAnnotationText>
      )}
      <GraphLayoutHeader>
        <GraphLayoutHeaderText>
          {props.title} {props.popoverText && <ChartPopover>{props.popoverText}</ChartPopover>}
          {props.titleMessage && <TitleMessage>{props.titleMessage}</TitleMessage>}
        </GraphLayoutHeaderText>
        {props.legend && <GraphLayoutHeaderLegend>{props.legend}</GraphLayoutHeaderLegend>}
      </GraphLayoutHeader>
      <GraphLayoutLeftLabel>
        <GraphLayoutLeftLabelText>
          <WorkloadLabel>IOPS</WorkloadLabel>
        </GraphLayoutLeftLabelText>
      </GraphLayoutLeftLabel>

      <GraphLayoutBottomLabel>
        <svg preserveAspectRatio="xMinYMin" width="100%" viewBox={`0, 0, ${width}, 40`} height={40}>
          <ZoomPercentageBar
            width={width}
            height={10}
            color="#9AD3FD"
            x={0}
            y={10}
            onZoom={onZoom}
          />
        </svg>

        <div style={{ width: '100%' }}>
          <WorkloadBottomLabel>Capacity</WorkloadBottomLabel>
        </div>
      </GraphLayoutBottomLabel>
      <VerticalLeftValueAxis
        color="#809DC4"
        percentage
        valueScale={yScale}
        height={height}
      ></VerticalLeftValueAxis>
      <HorizontalPercentageAxis
        color="#809DC4"
        scale={state.zoomedScale}
      ></HorizontalPercentageAxis>
    </GraphLayoutGrid>
  );
};
export default WorkloadConcentrationChart;
